/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AttributeCodeType,
  AttributeType,
  BTUsType,
  CondenserLocationType,
  ExistingSystemComponentType,
  ExistingSystemType,
  LevelType,
  PartialWithRequired,
  PositionType,
  ProjectAttachmentType,
  SatelliteViewType,
  SectionType,
  SizingCalculatorAttributesType,
  SpaceType,
  StructureItemTypeCode
} from 'data/types';
import {
  existingSystemAcceptsComponentType,
  isBlockedSelfContainedTypeChange
} from 'utils/existing-equipment';
import {
  nextExistingSystemName,
  renumberDefaultExistingSystemNames
} from 'utils/existing-system-names';
import { compositionPicksAreEqual } from 'utils/composition-picks';
import { rotateExposure } from 'utils/exposure-rotation';
import { FootprintCommitPayload } from 'utils/footprint-shapes-commit-types';
import { deriveOpenToFromOpenFloorPlan } from 'utils/open-to';
import { AutoExtendedEdge } from 'utils/space-polygon';
import { sortLevelsByVerticalPosition } from 'utils/structure-basics';
import { CompositionPicks } from 'utils/system-composition-types';
import type { PtacPreferencesRecord } from '@alpinehomeair/common';
import type { TonnageRecord } from 'utils/tonnage-logic';

// The two sides of a compatible-combinations check (AHA-6681 dual summary):
// 'kept' anchors the search on the kept components, 'alternatives' runs the
// codes-bounded unanchored search (keptComponentUniqueIds: []).
export type SystemCompatibleResultSet = 'kept' | 'alternatives';

// One system's mirrored selections record: the persisted
// RecommendationSelections shape minus the server-managed lastResults.
// Structural superset of the old { wantIds } entry — every legacy consumer
// keeps reading wantIds unchanged.
export type SystemRecommendationSelectionsEntry = {
  wantIds: number[];
  haveId?: number | null;
  refrigerant?: string | null;
  age?: string | null;
  useRecommendations?: boolean;
  fingerprint?: string;
  virtualCodes?: string[];
  virtualFuel?: string | null;
  // SYSTEM SIZE record (AHA-6681 tonnage); parsed via parseSavedTonnage
  // before it ever enters the mirror, so it is always well-formed here.
  tonnage?: TonnageRecord | null;
  ptac?: PtacPreferencesRecord | null;
};

// One side's check result — written live by the debounced on-step refetch
// and the Next-click fetch, or restored from the persisted
// RecommendationSelections JSON via the strip's context GET.
export type SystemCompatibleResultsEntry = {
  status: 'loading' | 'loaded' | 'error';
  fetchedAt: string | null;
  totalCount: number;
  // Always the recommended-set facet since sub-build 3 (the check runs
  // unconditionally recommended-bounded): Choose Systems bounds this
  // system's contribution to the union of these code lists.
  systemTypes: { code: string; label: string; count: number }[];
  // Hash of the latest check request issued for this (system, resultSet);
  // finish/fail only apply when their hash still matches (latest-wins stale
  // guard, same pattern as ducted-compatibility). Doubles as the thunk's
  // dedupe key. Optional on purpose: a restored entry never carries one, so
  // the first live check after a restore always refetches once.
  requestBodyHash?: string;
};

// The compatibleResultsBySystem value (AHA-6681 dual summary): 'alternatives'
// always exists once a check has run; 'kept' exists only when >=1 component
// was kept at check time. Feeds the step's display panel and the Choose
// Systems tab signal.
export type SystemCompatibleResultsType = Partial<
  Record<SystemCompatibleResultSet, SystemCompatibleResultsEntry>
>;

// Sum of the present entries' totals — the "any compatible system exists"
// number for the Choose Systems tab signal and the step's zero-state.
export function combinedCompatibleTotal(
  record: SystemCompatibleResultsType | undefined
): number {
  return (
    (record?.kept?.totalCount ?? 0) + (record?.alternatives?.totalCount ?? 0)
  );
}

export type StructureImageAssignmentType = {
  structureItemUniqueId?: string;
  structureItemTypeCode?: StructureItemTypeCode;
  scale?: number | null;
  rotation?: number | null;
  startPositionX?: number | null;
  startPositionY?: number | null;
};

export interface StructureState {
  structureUniqueId: string | null;
  structureId: number | null;
  structureName: string | null;
  structureNameUserEdited: boolean;
  structureActive: boolean | null;
  structureOptions: { label: string; uniqueId: string }[];
  structure: LevelType[];
  structureTypeId: number | null;
  structureSubtypeId: number | null;
  partialStructure: boolean | null;
  conditionedLevels: string[];
  supplementalMiniSplitSpaces: string[];
  existingSystems: ExistingSystemType[];
  notes: string;
  structureAttachments: ProjectAttachmentType[];
  structureImageAssignments: Record<
    number,
    StructureImageAssignmentType | null
  >;
  satelliteView: SatelliteViewType | null;
  footprint: PositionType[] | null;
  condenserLocations: CondenserLocationType[] | null;
  spaceExtensionRecords: Record<string, AutoExtendedEdge[]>;
  compatibleResultsBySystem: Record<string, SystemCompatibleResultsType>;
  // Redux mirror of each system's persisted recommendation-strip selections
  // (AHA-6681 batch 1, widened to the FULL record for the tonnage feature):
  // the ducted gate reads wantIds, the tonnage surfaces (skip routing, L&S
  // conflict dialog, CS filter seed) read tonnage, and the out-of-strip
  // persist helper needs every field because the selections PATCH replaces
  // the whole record (only lastResults is server-preserved). Hydrated at
  // structure load from the per-system recommendation-context GET and kept
  // live by the page's strip wrappers. A null entry means "context fetched,
  // nothing saved".
  recommendationSelectionsBySystem: Record<
    string,
    SystemRecommendationSelectionsEntry | null
  >;
  // Per-system full-universe catalog code union (AHA-6796 fuel vocabulary):
  // the deduped union of context.systemTypes[].compatibilityCodes from the
  // same per-system recommendation-context GETs that hydrate the mirror
  // above — the honest "every eligible ducted type" universe the fuel chip
  // rows derive their vocabulary from (the recommended bound alone
  // collapses to the derived fuel). Written at structure-load hydration and
  // kept live by the strip's context refetches + the CS healer thunk; a
  // missing entry falls back to the recommended bound (ductedUniverseCodes).
  recommendationCatalogCodesBySystem: Record<string, string[]>;
  // False until the structure-load hydration lands: the page-progress
  // watcher treats unhydrated as 0% so a reload can never unlock the ducted
  // gate on stale/empty state.
  recommendationSelectionsLoaded: boolean;
  wantCompositionPicksBySystem: Record<string, CompositionPicks>;
  // Monotonic nonce bumped by the autosave watcher AFTER a saveBaseStructure
  // whose keep/replace flags or component set changed lands (AHA-6681 QA
  // F2). The Equipment Details strip hosts fold it into componentsKey so the
  // recommendation-context GET refires against the SAVED state — the
  // flip-triggered GET is one-mutation-behind the 500ms debounced save and
  // reconciles against the pre-save derivation, so the fingerprint-overwrite
  // reseed would otherwise never run in-session.
  equipmentSaveVersion: number;
  // Monotonic nonce bumped by the autosave watcher after EVERY successful
  // saveBaseStructure — spec/model edits included (AHA-6796). Consumed by
  // the confidence-score refetch, which must track all persisted equipment
  // state; distinct from equipmentSaveVersion, which stays derivation-only
  // for the recommendation strips.
  equipmentPersistVersion: number;
  // Structure autosave in flight: the watcher requests a sequence number
  // when it schedules a levels/sections/spaces save and completes it when
  // that save (and its sizing follow-up) settles. Pending while
  // requested > completed; read with selectStructureSavePending by the
  // valid-systems watcher, which holds its full request until the rows the
  // server scores against have landed.
  structureSaveRequestedSeq: number;
  structureSaveCompletedSeq: number;
  // True from the moment a structure is created in THIS page load until its
  // insulation defaults have been seeded from the fresh year-built
  // recommendation (utils/sizing seedDefaultSizingItemInsulationValues).
  // Structure switches always reload the page, so the flag can only ever
  // describe the current structure; refetches of the same structure must not
  // clear it.
  isNewlyCreatedStructure: boolean;
  // One-shot, in-memory only (AHA-6784): armed by the hybrid autobuild hook
  // right after it sets the generated structure, consumed by the autosave
  // watcher after the first server sizing generation to bulk-generate
  // windows once. Deliberately excluded from every save payload — like
  // isNewlyCreatedStructure/equipmentSaveVersion it is watcher-coordination
  // state, never serialized.
  hybridAutobuildWindowsPending: boolean;
  autoLayoutSpaceIds: string[];
  ductedSlotCopiesInFlight: string[];
}

export const defaultStructureState: StructureState = {
  structureUniqueId: null,
  structureId: null,
  structureName: null,
  structureNameUserEdited: false,
  structureActive: null,
  structureOptions: [],
  structure: [],
  structureTypeId: null,
  structureSubtypeId: null,
  partialStructure: null,
  conditionedLevels: [],
  supplementalMiniSplitSpaces: [],
  existingSystems: [],
  notes: '',
  structureAttachments: [],
  structureImageAssignments: {},
  satelliteView: null,
  footprint: null,
  condenserLocations: null,
  spaceExtensionRecords: {},
  compatibleResultsBySystem: {},
  recommendationSelectionsBySystem: {},
  recommendationCatalogCodesBySystem: {},
  recommendationSelectionsLoaded: false,
  wantCompositionPicksBySystem: {},
  equipmentSaveVersion: 0,
  equipmentPersistVersion: 0,
  structureSaveRequestedSeq: 0,
  structureSaveCompletedSeq: 0,
  isNewlyCreatedStructure: false,
  hybridAutobuildWindowsPending: false,
  autoLayoutSpaceIds: [],
  ductedSlotCopiesInFlight: []
};

type StructureItemPath =
  | { type: 'level'; levelIdx: number }
  | { type: 'section'; levelIdx: number; sectionIdx: number }
  | {
      type: 'space';
      levelIdx: number;
      sectionIdx: number;
      spaceIdx: number;
    };

function buildStructureItemPathIndex(
  structure: LevelType[]
): Map<string, StructureItemPath> {
  const index = new Map<string, StructureItemPath>();
  structure.forEach((level, levelIdx) => {
    index.set(level.uniqueId, { type: 'level', levelIdx });
    level.sections.forEach((section, sectionIdx) => {
      index.set(section.uniqueId, { type: 'section', levelIdx, sectionIdx });
      section.spaces.forEach((space, spaceIdx) => {
        index.set(space.uniqueId, {
          type: 'space',
          levelIdx,
          sectionIdx,
          spaceIdx
        });
      });
    });
  });
  return index;
}

const structureSlice = createSlice({
  name: 'structure',
  initialState: defaultStructureState,
  reducers: {
    setStructureUniqueId: (state, action: PayloadAction<string | null>) => {
      // Only reset user-edited flag when switching away from an existing structure,
      // not when a new structure gets its first ID assigned via autosave
      if (
        state.structureUniqueId !== null &&
        state.structureUniqueId !== action.payload
      ) {
        state.structureNameUserEdited = false;
      }
      state.structureUniqueId = action.payload;
    },
    setIsNewlyCreatedStructure: (state, action: PayloadAction<boolean>) => {
      state.isNewlyCreatedStructure = action.payload;
    },
    setHybridAutobuildWindowsPending: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.hybridAutobuildWindowsPending = action.payload;
    },
    addAutoLayoutSpaceIds: (state, action: PayloadAction<string[]>) => {
      const next = new Set(state.autoLayoutSpaceIds);
      action.payload.forEach((id) => {
        next.add(id);
      });
      state.autoLayoutSpaceIds = [...next];
    },
    removeAutoLayoutSpaceIds: (state, action: PayloadAction<string[]>) => {
      const remove = new Set(action.payload);
      state.autoLayoutSpaceIds = state.autoLayoutSpaceIds.filter(
        (id) => !remove.has(id)
      );
    },
    setAutoLayoutSpaceIds: (state, action: PayloadAction<string[]>) => {
      state.autoLayoutSpaceIds = action.payload;
    },
    setStructureId: (state, action: PayloadAction<number | null>) => {
      state.structureId = action.payload;
    },
    setStructureActive: (state, action: PayloadAction<boolean | null>) => {
      state.structureActive = action.payload;
    },
    setStructureName: (state, action: PayloadAction<string | null>) => {
      state.structureName = action.payload;
      if (state.structureUniqueId && action.payload) {
        const optionIndex = state.structureOptions.findIndex(
          (opt) => opt.uniqueId === state.structureUniqueId
        );
        if (optionIndex !== -1) {
          state.structureOptions[optionIndex].label = action.payload;
        }
      }
    },
    setStructureNameByUser: (state, action: PayloadAction<string>) => {
      state.structureName = action.payload;
      state.structureNameUserEdited = true;
      if (state.structureUniqueId && action.payload) {
        const optionIndex = state.structureOptions.findIndex(
          (opt) => opt.uniqueId === state.structureUniqueId
        );
        if (optionIndex !== -1) {
          state.structureOptions[optionIndex].label = action.payload;
        }
      }
    },
    setStructureOptions: (
      state,
      action: PayloadAction<{ label: string; uniqueId: string }[]>
    ) => {
      state.structureOptions = action.payload;
    },
    restoreStructure: (
      state,
      action: PayloadAction<{
        structure: LevelType[];
        spaceExtensionRecords: Record<string, AutoExtendedEdge[]>;
        structureImageAssignments: Record<
          number,
          StructureImageAssignmentType | null
        >;
        autoLayoutSpaceIds?: string[];
      }>
    ) => {
      state.structure = action.payload.structure;
      state.spaceExtensionRecords = action.payload.spaceExtensionRecords;
      state.structureImageAssignments =
        action.payload.structureImageAssignments;
      if (action.payload.autoLayoutSpaceIds) {
        state.autoLayoutSpaceIds = action.payload.autoLayoutSpaceIds;
      }
    },
    setStructure: (state, action: PayloadAction<LevelType[]>) => {
      const previousSectionsWithSpaces = new Set<string>();
      state.structure.forEach((level) => {
        level.sections.forEach((section) => {
          if (section.spaces.length > 0) {
            previousSectionsWithSpaces.add(section.uniqueId);
          }
        });
      });

      const migratedPayload = deriveOpenToFromOpenFloorPlan(action.payload);

      state.structure = migratedPayload.map((level) => ({
        ...level,
        sections: level.sections.filter((section) => {
          const hadSpaces = previousSectionsWithSpaces.has(section.uniqueId);
          const hasSpaces = section.spaces.length > 0;
          return hasSpaces || !hadSpaces;
        })
      }));
    },
    normalizeLevelOrder: (state) => {
      state.structure = sortLevelsByVerticalPosition(state.structure);
    },
    updateStructureItem: (
      state,
      action: PayloadAction<
        PartialWithRequired<LevelType | SectionType | SpaceType, 'uniqueId'> & {
          sizingAttributes?: SizingCalculatorAttributesType;
          modified?: boolean;
          openTo?: string[];
        }
      >
    ) => {
      const item = action.payload;
      const pathIndex = buildStructureItemPathIndex(state.structure);
      const path = pathIndex.get(item.uniqueId);
      if (!path) return;

      if (path.type === 'level') {
        const level = state.structure[path.levelIdx];
        Object.keys(item).forEach((key) => {
          level[key] = item[key];
        });
        return;
      }

      if (path.type === 'section') {
        const section =
          state.structure[path.levelIdx].sections[path.sectionIdx];
        Object.keys(item).forEach((key) => {
          section[key] = item[key];
        });
        return;
      }

      const space =
        state.structure[path.levelIdx].sections[path.sectionIdx].spaces[
          path.spaceIdx
        ];
      Object.keys(item).forEach((key) => {
        space[key] = item[key];
      });
      if (item.sizingAttributes) {
        space.sizingAttributes = { ...item.sizingAttributes };
      }
      if (typeof item.modified === 'boolean') {
        space.modified = item.modified;
      }
    },
    updateStructureItemAttribute: (
      state,
      action: PayloadAction<
        | {
            uniqueId: string;
            attributeId: number;
            value: any;
          }
        | {
            uniqueId: string;
            attributeCode: AttributeCodeType;
            value: any;
          }
      >
    ) => {
      if ('attributeId' in action.payload) {
        const { uniqueId, attributeId, value } = action.payload;
        const pathIndex = buildStructureItemPathIndex(state.structure);
        const path = pathIndex.get(uniqueId);

        if (path?.type === 'level') {
          const level = state.structure[path.levelIdx];
          const attributeIndex = level.attributes.findIndex(
            (a) => a.id === attributeId
          );

          if (attributeIndex !== -1) {
            level.attributes[attributeIndex].value = value;
            level.attributes[attributeIndex].modified = true;
            level.modified = true;
          }
        } else if (path?.type === 'space') {
          const space =
            state.structure[path.levelIdx].sections[path.sectionIdx].spaces[
              path.spaceIdx
            ];
          const attributeIndex = space.attributes.findIndex(
            (a) => a.id === attributeId
          );

          if (attributeIndex !== -1) {
            space.attributes[attributeIndex].value = value;
            space.attributes[attributeIndex].modified = true;
            space.modified = true;
          }
        }
      } else {
        const { uniqueId, attributeCode, value } = action.payload;
        const pathIndex = buildStructureItemPathIndex(state.structure);
        const path = pathIndex.get(uniqueId);

        if (path?.type !== 'space') return;

        const space =
          state.structure[path.levelIdx].sections[path.sectionIdx].spaces[
            path.spaceIdx
          ];
        const attributeIndex = space.attributes.findIndex(
          (a) => a.code === attributeCode
        );
        if (attributeIndex !== -1) {
          space.attributes[attributeIndex].value = value;
          space.attributes[attributeIndex].modified = true;
          space.modified = true;
        }
      }
    },
    updateSpaceSizingAttributes: (
      state,
      action: PayloadAction<{ uniqueId: string }>
    ) => {
      const { uniqueId } = action.payload;

      const updateSpaceSizing = (space: any) => {
        if (space.uniqueId !== uniqueId || !space.sizingAttributes) {
          return;
        }

        const squareFootage = Math.round(
          space.attributes.find((a: any) => a.code === 'square_footage')
            ?.value ?? 0
        );
        const ceilingHeight =
          space.attributes.find((a: any) => a.code === 'ceiling_height')
            ?.value ?? 0;
        const wallWidth = Math.ceil(Math.sqrt(squareFootage));
        const numberofAboveGradeWalls =
          space.sizingAttributes.aboveGradeWalls.length;

        space.sizingAttributes.ceilingsBelowAttic?.forEach((ceiling: any) => {
          ceiling.area = squareFootage;
        });

        space.sizingAttributes.ceilingsBelowRoof?.forEach((ceiling: any) => {
          ceiling.area = squareFootage;
        });

        space.sizingAttributes.floorsOverExposedSpace?.forEach((floor: any) => {
          floor.area = squareFootage;
        });

        space.sizingAttributes.floorsOverUnconditionedSpace?.forEach(
          (floor: any) => {
            floor.area = squareFootage;
          }
        );

        space.sizingAttributes.basementFloors?.forEach((floor: any) => {
          floor.area = squareFootage;
        });

        space.sizingAttributes.slabFloors?.forEach((floor: any) => {
          floor.exposedEdgeFeet = wallWidth * numberofAboveGradeWalls;
        });

        space.sizingAttributes.aboveGradeWalls?.forEach((wall: any) => {
          wall.width = wallWidth;
          wall.height = ceilingHeight;
        });

        space.sizingAttributes.belowGradeWalls?.forEach((wall: any) => {
          wall.width = wallWidth;
          wall.height = ceilingHeight;
        });
      };

      state.structure.forEach((level) => {
        level.sections.forEach((section) => {
          section.spaces.forEach(updateSpaceSizing);
        });
      });
    },
    applyAttributesToEntireLevel: (
      state,
      action: PayloadAction<{ levelId: string; attributes: AttributeType[] }>
    ) => {
      const { levelId, attributes } = action.payload;
      state.structure.forEach((level) => {
        if (level.uniqueId === levelId) {
          level.sections.forEach((section) => {
            section.spaces.forEach((space) => {
              attributes.forEach((attribute) => {
                const attributeIndex = space.attributes.findIndex(
                  (a) => a.code === attribute.code
                );
                if (attributeIndex !== -1) {
                  space.attributes[attributeIndex].value = attribute.value;
                  space.attributes[attributeIndex].modified = true;
                  level.modified = true;
                }
              });
            });
          });
        }
      });
    },
    rotateAllWallExposures: (
      state,
      action: PayloadAction<{ delta: number }>
    ) => {
      const { delta } = action.payload;
      if (delta === 0) return;

      state.structure.forEach((level) => {
        level.sections.forEach((section) => {
          section.spaces.forEach((space) => {
            if (!space.sizingAttributes) return;

            space.sizingAttributes.aboveGradeWalls?.forEach((wall) => {
              const rotated = rotateExposure(wall.exposure, delta);
              if (rotated) {
                wall.exposure = rotated;
              }
            });

            space.sizingAttributes.belowGradeWalls?.forEach((wall) => {
              const rotated = rotateExposure(wall.exposure, delta);
              if (rotated) {
                wall.exposure = rotated;
              }
            });
          });
        });
      });
    },
    updateStructureBTUs: (
      state,
      action: PayloadAction<{ [uniqueId: string]: BTUsType }>
    ) => {
      const newBTUs = action.payload;
      state.structure.forEach((level) => {
        level.sections.forEach((section) => {
          section.spaces.forEach((space) => {
            if (newBTUs[space.uniqueId]) {
              space.BTUs = newBTUs[space.uniqueId];
            }
          });
        });
      });
    },
    deleteStructureItem: (
      state,
      action: PayloadAction<{ itemId: number } | { uniqueId: string }>
    ) => {
      const sectionsWithSpaces = new Set<string>();
      state.structure.forEach((level) => {
        level.sections.forEach((section) => {
          if (section.spaces.length > 0) {
            sectionsWithSpaces.add(section.uniqueId);
          }
        });
      });

      if ('itemId' in action.payload) {
        const { itemId } = action.payload;
        const removedIds: string[] = [];
        state.structure.forEach((level) => {
          level.sections.forEach((section) => {
            section.spaces.forEach((space, spaceIndex) => {
              if (space.itemId === itemId) {
                removedIds.push(space.uniqueId);
                section.spaces.splice(spaceIndex, 1);
              }
            });
          });
        });
        if (removedIds.length > 0) {
          const remove = new Set(removedIds);
          state.autoLayoutSpaceIds = state.autoLayoutSpaceIds.filter(
            (id) => !remove.has(id)
          );
        }
      } else {
        const { uniqueId } = action.payload;
        state.structure.forEach((level, levelIndex) => {
          if (level.uniqueId === uniqueId) {
            state.structure.splice(levelIndex, 1);
          } else {
            level.sections.forEach((section, sectionIndex) => {
              if (section.uniqueId === uniqueId) {
                level.sections.splice(sectionIndex, 1);
              } else {
                section.spaces.forEach((space, spaceIndex) => {
                  if (space.uniqueId === uniqueId) {
                    section.spaces.splice(spaceIndex, 1);
                  }
                });
              }
            });
          }
        });

        const updatedAssignments: Record<
          number,
          StructureImageAssignmentType | null
        > = {};
        Object.entries(state.structureImageAssignments).forEach(
          ([attachmentIdStr, assignment]) => {
            const attachmentId = Number(attachmentIdStr);
            if (assignment?.structureItemUniqueId === uniqueId) {
              updatedAssignments[attachmentId] = null;
            } else {
              updatedAssignments[attachmentId] = assignment;
            }
          }
        );
        state.structureImageAssignments = updatedAssignments;

        delete state.spaceExtensionRecords[uniqueId];
        state.autoLayoutSpaceIds = state.autoLayoutSpaceIds.filter(
          (id) => id !== uniqueId
        );
      }

      state.structure.forEach((level) => {
        level.sections = level.sections.filter((section) => {
          const hadSpaces = sectionsWithSpaces.has(section.uniqueId);
          const hasSpaces = section.spaces.length > 0;
          return hasSpaces || !hadSpaces;
        });
      });
    },
    setStructureLoading: (state, action: PayloadAction<boolean>) => {
      state.structure.forEach((level) => {
        level.sections.forEach((section) => {
          section.spaces.forEach((space) => {
            space.loading = action.payload;
          });
        });
      });
    },
    setConditionedLevels: (state, action: PayloadAction<string[]>) => {
      state.conditionedLevels = action.payload;
    },
    setSupplementalMiniSplitSpaces: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.supplementalMiniSplitSpaces = action.payload;
    },
    setExistingSystems: (
      state,
      action: PayloadAction<ExistingSystemType[]>
    ) => {
      state.existingSystems = action.payload;
      const remaining = new Set(
        action.payload.map((system) => system.uniqueId)
      );
      Object.keys(state.wantCompositionPicksBySystem).forEach(
        (systemUniqueId) => {
          if (!remaining.has(systemUniqueId)) {
            delete state.wantCompositionPicksBySystem[systemUniqueId];
          }
        }
      );
    },
    updateExistingSystem: (
      state,
      action: PayloadAction<PartialWithRequired<ExistingSystemType, 'uniqueId'>>
    ) => {
      const { uniqueId, ...rest } = action.payload;
      const systemIndex = state.existingSystems.findIndex(
        (s) => s.uniqueId === uniqueId
      );
      if (systemIndex !== -1) {
        state.existingSystems[systemIndex] = {
          ...state.existingSystems[systemIndex],
          ...rest
        };
      }
    },
    updateExistingSystemComponent: (
      state,
      action: PayloadAction<
        PartialWithRequired<ExistingSystemComponentType, 'uniqueId'>
      >
    ) => {
      const { uniqueId, ...rest } = action.payload;
      const systemIndex = state.existingSystems.findIndex((s) =>
        s.components.some((c) => c.uniqueId === uniqueId)
      );
      if (systemIndex !== -1) {
        const system = state.existingSystems[systemIndex];
        const componentIndex = system.components.findIndex(
          (c) => c.uniqueId === uniqueId
        );
        if (componentIndex !== -1) {
          const currentComponent = system.components[componentIndex];
          // SC mixing guard, belt-and-braces (AHA-6796, cursor[bot] PR
          // #4330): an SC (packaged) component must never sit beside split
          // components, and this reducer is dispatched from many sites
          // (modal save, details form, keep-toggle, four sites in
          // equipment-images) — mirroring addExistingEquipmentComponent's
          // role as the mixing-prevention layer here makes the invariant
          // hold no matter which future caller forgets its own guard.
          // Strip `type` AND `typeSpecified` when the payload would CHANGE
          // a sibling'd component's type to Self Contained. The same-type
          // carve-out (payload type === current type) keeps legacy mixed
          // data editable: editing a legacy SC component that already has
          // siblings re-asserts its own type — stripping there would
          // silently un-type a typed component and lose data while
          // introducing no new mixing, and it keeps the modal's residual
          // handleSave path coherent (selectedType can be SC with siblings
          // only via that legacy-SC edit, where `replacing: true` is
          // genuinely correct). `replacing` is deliberately NOT stripped
          // alongside the type: replacing: true is a valid state for a
          // split component (the card keep-toggle sets it), so a payload
          // whose SC-conditioned replacing: true survives a stripped type
          // stays coherent. The condition is the shared
          // isBlockedSelfContainedTypeChange predicate (cursor[bot] PR
          // #4330): the modal's tier-2 kind filter and save guard consume
          // the same definition, so the pickers enforce the carve-out
          // assumption this strip relies on instead of offering changes it
          // would silently refuse.
          if (
            isBlockedSelfContainedTypeChange(
              rest.type,
              currentComponent.type,
              system.components.length > 1
            )
          ) {
            delete rest.type;
            delete rest.typeSpecified;
          }
          system.components[componentIndex] = {
            ...currentComponent,
            ...rest
          } as ExistingSystemComponentType;
        }
      }
    },
    // Eager anchor for the no-equipment recommendations flow (AHA-6681):
    // the Existing Equipment page dispatches this on ducted-pathway mount
    // with zero systems, so the strip has a persisted row to anchor its
    // context GET / selections PATCH against. Idempotent at the reducer
    // level — a no-op while ANY system exists — so StrictMode double
    // effects and page remounts can never accumulate rows. When the user
    // later adds real equipment, addExistingEquipmentComponent reuses this
    // same ducted row, so the component lands in it.
    addEmptyExistingSystem: (
      state,
      action: PayloadAction<{
        uniqueId: string;
        type?: ExistingSystemType['type'];
      }>
    ) => {
      if (state.existingSystems.length > 0) return;
      state.existingSystems.push({
        uniqueId: action.payload.uniqueId,
        name: 'System 1',
        type: action.payload.type ?? 'ducted',
        components: []
      });
    },
    // Targeting is the mixing-prevention layer (AHA-6796): a packaged
    // (Self Contained) unit is a complete standalone system, so an SC add
    // may only land in a zero-component ducted system (the eager anchor
    // row) — never alongside split components — and a split add must skip
    // any SC-containing system. An explicit targetSystemUniqueId (the
    // per-system add tiles) wins whenever that system accepts the
    // component; otherwise the first accepting system is used. Everything
    // falling through creates a new system from the payload's
    // newSystemUniqueId.
    addExistingEquipmentComponent: (
      state,
      action: PayloadAction<{
        component: ExistingSystemComponentType;
        newSystemUniqueId: string;
        targetSystemUniqueId?: string;
      }>
    ) => {
      const { component, newSystemUniqueId, targetSystemUniqueId } =
        action.payload;
      const accepts = (system: ExistingSystemType) =>
        existingSystemAcceptsComponentType(system, component.type);
      const targetSystem = state.existingSystems.find(
        (s) => s.uniqueId === targetSystemUniqueId
      );
      let system =
        targetSystem && accepts(targetSystem)
          ? targetSystem
          : state.existingSystems.find(accepts);
      if (!system) {
        system = {
          uniqueId: newSystemUniqueId,
          name: nextExistingSystemName(state.existingSystems),
          type: 'ducted',
          components: []
        };
        state.existingSystems.push(system);
      }
      system.components.push(component);
    },
    addExistingSystem: (state, action: PayloadAction<{ uniqueId: string }>) => {
      state.existingSystems.push({
        uniqueId: action.payload.uniqueId,
        name: nextExistingSystemName(state.existingSystems),
        type: 'ducted',
        components: []
      });
    },
    removeExistingSystem: (
      state,
      action: PayloadAction<{ uniqueId: string }>
    ) => {
      const { uniqueId } = action.payload;
      if (state.existingSystems.length < 2) return;
      if (!state.existingSystems.some((s) => s.uniqueId === uniqueId)) return;
      state.existingSystems = renumberDefaultExistingSystemNames(
        state.existingSystems.filter((system) => system.uniqueId !== uniqueId)
      );
      delete state.recommendationSelectionsBySystem[uniqueId];
      delete state.compatibleResultsBySystem[uniqueId];
      delete state.recommendationCatalogCodesBySystem[uniqueId];
      delete state.wantCompositionPicksBySystem[uniqueId];
    },
    deleteExistingSystemComponent: (
      state,
      action: PayloadAction<{ uniqueId: string }>
    ) => {
      const { uniqueId } = action.payload;
      const emptiedSystemIds: string[] = [];
      state.existingSystems.forEach((system) => {
        const before = system.components.length;
        system.components = system.components.filter(
          (c) => c.uniqueId !== uniqueId
        );
        if (before > 0 && system.components.length === 0) {
          emptiedSystemIds.push(system.uniqueId);
        }
      });
      // A delete that empties a SECONDARY system removes the system itself
      // (AHA-6796) — a leftover empty row would mount an incomplete strip
      // and pin the page gate at 0%. The single-system case keeps today's
      // keep-the-empty-row behavior (the no-equipment anchor semantics).
      // Per-system mirror entries go with the row for coherence.
      if (emptiedSystemIds.length > 0 && state.existingSystems.length > 1) {
        state.existingSystems = state.existingSystems.filter(
          (system) => !emptiedSystemIds.includes(system.uniqueId)
        );
        emptiedSystemIds.forEach((systemUniqueId) => {
          delete state.recommendationSelectionsBySystem[systemUniqueId];
          delete state.compatibleResultsBySystem[systemUniqueId];
        });
      }
    },
    // Un-place (AHA-6681 batch 2): key DELETION, not undefined assignment —
    // the persisted component must provably drop position/levelUniqueId
    // (ss-api zod schemas reject explicit null; the component update path is
    // full-replace so absent keys truly disappear server-side).
    unplaceExistingSystemComponent: (
      state,
      action: PayloadAction<{ uniqueId: string }>
    ) => {
      const { uniqueId } = action.payload;
      for (const system of state.existingSystems) {
        const component = system.components.find(
          (c) => c.uniqueId === uniqueId
        );
        if (component) {
          delete (component as { position?: unknown }).position;
          delete (component as { levelUniqueId?: unknown }).levelUniqueId;
          return;
        }
      }
    },
    beginSystemCompatibleCheck: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        // The sets in play for this run: entries for any OTHER set are
        // pruned atomically (a kept→empty transition must not leave a stale
        // 'kept' entry feeding the union bound).
        resultSets: SystemCompatibleResultSet[];
        // Only the sets actually (re)fetching carry a hash; an in-play set
        // absent here (deduped by the thunk) is left untouched.
        hashes: Partial<Record<SystemCompatibleResultSet, string>>;
      }>
    ) => {
      const { systemUniqueId, resultSets, hashes } = action.payload;
      const record = state.compatibleResultsBySystem[systemUniqueId] ?? {};
      (Object.keys(record) as SystemCompatibleResultSet[]).forEach(
        (resultSet) => {
          if (!resultSets.includes(resultSet)) delete record[resultSet];
        }
      );
      resultSets.forEach((resultSet) => {
        const requestBodyHash = hashes[resultSet];
        if (requestBodyHash === undefined) return;
        const existing = record[resultSet];
        // Previous data stays visible under the loading state; the new hash
        // becomes the only one finish/fail may apply (latest-wins).
        record[resultSet] = existing
          ? {
              ...existing,
              status: 'loading',
              requestBodyHash
            }
          : {
              status: 'loading',
              fetchedAt: null,
              totalCount: 0,
              systemTypes: [],
              requestBodyHash
            };
      });
      state.compatibleResultsBySystem[systemUniqueId] = record;
    },
    finishSystemCompatibleCheck: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        resultSet: SystemCompatibleResultSet;
        requestBodyHash: string;
        fetchedAt: string;
        totalCount: number;
        systemTypes: { code: string; label: string; count: number }[];
      }>
    ) => {
      const {
        systemUniqueId,
        resultSet,
        requestBodyHash,
        fetchedAt,
        totalCount,
        systemTypes
      } = action.payload;
      const existing =
        state.compatibleResultsBySystem[systemUniqueId]?.[resultSet];
      // Stale response (a newer begin replaced the hash): drop it.
      if (!existing || existing.requestBodyHash !== requestBodyHash) return;
      // Latest fetch wins — including a fresh zero over a stale positive.
      state.compatibleResultsBySystem[systemUniqueId][resultSet] = {
        status: 'loaded',
        fetchedAt,
        totalCount,
        systemTypes,
        requestBodyHash
      };
    },
    failSystemCompatibleCheck: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        resultSet: SystemCompatibleResultSet;
        requestBodyHash: string;
      }>
    ) => {
      const { systemUniqueId, resultSet, requestBodyHash } = action.payload;
      const existing =
        state.compatibleResultsBySystem[systemUniqueId]?.[resultSet];
      // Stale failure (a newer begin replaced the hash): drop it.
      if (!existing || existing.requestBodyHash !== requestBodyHash) return;
      // Previous data preserved — a failed refresh keeps the last results.
      state.compatibleResultsBySystem[systemUniqueId][resultSet] = {
        ...existing,
        status: 'error'
      };
    },
    restoreSystemCompatibleResults: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        results: SystemCompatibleResultsType;
      }>
    ) => {
      const { systemUniqueId, results } = action.payload;
      // Set-if-absent: a persisted restore never clobbers a live in-session
      // fetch (a wrapper remount re-fires the restore callback).
      if (!state.compatibleResultsBySystem[systemUniqueId]) {
        state.compatibleResultsBySystem[systemUniqueId] = results;
      }
    },
    // Structure-switch reset for the ducted-gate mirror: dispatched at the
    // start of loadStructure so a stale loaded=true from the previous
    // structure can never unlock the gate during the hydration window.
    resetRecommendationSelections: (state) => {
      state.recommendationSelectionsBySystem = {};
      // The catalog mirror goes with it (AHA-6796): a structure switch must
      // never feed the previous structure's universe to the fuel rows.
      state.recommendationCatalogCodesBySystem = {};
      state.wantCompositionPicksBySystem = {};
      state.recommendationSelectionsLoaded = false;
    },
    clearExistingEquipmentForNoAnswer: (state) => {
      state.existingSystems = [];
      state.recommendationSelectionsBySystem = {};
      state.recommendationCatalogCodesBySystem = {};
      state.wantCompositionPicksBySystem = {};
    },
    setWantCompositionPicks: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        picks: CompositionPicks;
      }>
    ) => {
      const { systemUniqueId, picks } = action.payload;
      if (
        compositionPicksAreEqual(
          state.wantCompositionPicksBySystem[systemUniqueId],
          picks
        )
      ) {
        return;
      }
      state.wantCompositionPicksBySystem[systemUniqueId] = picks;
    },
    // Bulk hydration from the per-system recommendation-context GETs at
    // structure load; flips the loaded flag even when every entry is null.
    hydrateRecommendationSelections: (
      state,
      action: PayloadAction<
        Record<string, SystemRecommendationSelectionsEntry | null>
      >
    ) => {
      state.recommendationSelectionsBySystem = action.payload;
      state.recommendationSelectionsLoaded = true;
    },
    // Bulk load-time write of the per-system catalog code unions (AHA-6796
    // fuel vocabulary), from the SAME context GETs as the selections
    // hydration above — dispatched BEFORE hydrateRecommendationSelections
    // so the recommendationSelectionsLoaded gate guarantees catalogs are
    // present by the time the context reset arms. Fills only ABSENT keys:
    // any entry already in the map was written after the loadStructure
    // reset by a strip context refetch or the CS healer — triggered later,
    // hence at least as fresh — and must survive this late-arriving bulk
    // write. (Single wholesale assignment on purpose: the reset hook's
    // universe memo keys on this map's identity.)
    hydrateRecommendationCatalogCodes: (
      state,
      action: PayloadAction<Record<string, string[]>>
    ) => {
      state.recommendationCatalogCodesBySystem = {
        ...action.payload,
        ...state.recommendationCatalogCodesBySystem
      };
    },
    // Live per-system catalog upsert from the strip's context refetches and
    // the CS missing-summary healer (AHA-6796). Content-compared no-op so
    // the frequent equal-content refetches never churn references (the
    // reset hook's universe memo keys on this map's identity).
    setSystemRecommendationCatalogCodes: (
      state,
      action: PayloadAction<{ systemUniqueId: string; codes: string[] }>
    ) => {
      const { systemUniqueId, codes } = action.payload;
      const existing = state.recommendationCatalogCodesBySystem[systemUniqueId];
      if (
        existing &&
        existing.length === codes.length &&
        existing.every((code, index) => code === codes[index])
      ) {
        return;
      }
      state.recommendationCatalogCodesBySystem[systemUniqueId] = codes;
    },
    // See equipmentSaveVersion in StructureState: dispatched only by the
    // autosave watcher after a derivation-relevant saveBaseStructure
    // succeeds. Monotonic — no reset on structure switch (the strips fully
    // reset on systemUniqueId, and a stale-but-larger nonce only changes the
    // key string, never its meaning).
    bumpEquipmentSaveVersion: (state) => {
      state.equipmentSaveVersion += 1;
    },
    // See equipmentPersistVersion in StructureState: dispatched by the
    // autosave watcher after EVERY successful saveBaseStructure (spec edits
    // included), never on failure. Same monotonic/no-reset semantics as
    // bumpEquipmentSaveVersion.
    bumpEquipmentPersistVersion: (state) => {
      state.equipmentPersistVersion += 1;
    },
    markStructureSaveRequested: (state, action: PayloadAction<number>) => {
      state.structureSaveRequestedSeq = Math.max(
        state.structureSaveRequestedSeq,
        action.payload
      );
    },
    markStructureSaveCompleted: (state, action: PayloadAction<number>) => {
      state.structureSaveCompletedSeq = Math.max(
        state.structureSaveCompletedSeq,
        action.payload
      );
    },
    // Live per-system mirror update from the Existing Equipment page's strip
    // wrappers (selection emits and applied restores) — full-record replace,
    // matching the PATCH semantics.
    setSystemRecommendationSelections: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        selections: SystemRecommendationSelectionsEntry;
      }>
    ) => {
      state.recommendationSelectionsBySystem[action.payload.systemUniqueId] =
        action.payload.selections;
    },
    beginDuctedSlotPreferencesCopy: (
      state,
      action: PayloadAction<{ targetSystemUniqueId: string }>
    ) => {
      const { targetSystemUniqueId } = action.payload;
      if (state.ductedSlotCopiesInFlight.includes(targetSystemUniqueId)) return;
      state.ductedSlotCopiesInFlight.push(targetSystemUniqueId);
    },
    endDuctedSlotPreferencesCopy: (
      state,
      action: PayloadAction<{ targetSystemUniqueId: string }>
    ) => {
      state.ductedSlotCopiesInFlight = state.ductedSlotCopiesInFlight.filter(
        (systemUniqueId) =>
          systemUniqueId !== action.payload.targetSystemUniqueId
      );
    },
    // Tonnage-only merge for the out-of-strip surfaces (L&S conflict dialog,
    // CS review affix / "Show all sizes" escape): the rest of the record is
    // preserved so the follow-up full-record PATCH built from this mirror
    // can't lose fields. No prior entry = nothing else was ever saved — an
    // honest empty record carries the tonnage.
    setSystemTonnageRecord: (
      state,
      action: PayloadAction<{ systemUniqueId: string; tonnage: TonnageRecord }>
    ) => {
      const { systemUniqueId, tonnage } = action.payload;
      const existing = state.recommendationSelectionsBySystem[systemUniqueId];
      state.recommendationSelectionsBySystem[systemUniqueId] = {
        ...(existing ?? { wantIds: [] }),
        tonnage
      };
    },
    setSystemPtacPreferences: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        ptac: PtacPreferencesRecord;
      }>
    ) => {
      const { systemUniqueId, ptac } = action.payload;
      const existing = state.recommendationSelectionsBySystem[systemUniqueId];
      state.recommendationSelectionsBySystem[systemUniqueId] = {
        ...(existing ?? { wantIds: [] }),
        ptac
      };
    },
    setStructureTypeId: (state, action: PayloadAction<number | null>) => {
      state.structureTypeId = action.payload;
    },
    setStructureSubtypeId: (state, action: PayloadAction<number | null>) => {
      state.structureSubtypeId = action.payload;
    },
    setPartialStructure: (state, action: PayloadAction<boolean | null>) => {
      state.partialStructure = action.payload;
    },
    setNotes: (state, action: PayloadAction<string>) => {
      state.notes = action.payload;
    },
    setStructureAttachments: (
      state,
      action: PayloadAction<ProjectAttachmentType[]>
    ) => {
      state.structureAttachments = action.payload;
    },
    setStructureImageAssignments: (
      state,
      action: PayloadAction<Record<number, StructureImageAssignmentType | null>>
    ) => {
      state.structureImageAssignments = action.payload;
    },
    setSatelliteView: (
      state,
      action: PayloadAction<SatelliteViewType | null>
    ) => {
      state.satelliteView = action.payload;
    },
    assignImageToStructureItem: (
      state,
      action: PayloadAction<{
        attachmentId: number;
        structureItemUniqueId: string;
        structureItemTypeCode: StructureItemTypeCode;
      }>
    ) => {
      state.structureImageAssignments[action.payload.attachmentId] = {
        structureItemUniqueId: action.payload.structureItemUniqueId,
        structureItemTypeCode: action.payload.structureItemTypeCode
      };
    },
    unassignImageFromStructureItem: (
      state,
      action: PayloadAction<{ attachmentId: number }>
    ) => {
      state.structureImageAssignments[action.payload.attachmentId] = null;
    },
    updateStructureImageTransform: (
      state,
      action: PayloadAction<{
        attachmentId: number;
        scale?: number | null;
        rotation?: number | null;
        startPositionX?: number | null;
        startPositionY?: number | null;
      }>
    ) => {
      const { attachmentId, scale, rotation, startPositionX, startPositionY } =
        action.payload;
      const existing = state.structureImageAssignments[attachmentId];

      state.structureImageAssignments[attachmentId] = {
        structureItemUniqueId: existing?.structureItemUniqueId,
        structureItemTypeCode: existing?.structureItemTypeCode,

        scale: scale !== undefined ? scale : existing?.scale ?? 1,
        rotation: rotation !== undefined ? rotation : existing?.rotation ?? 0,
        startPositionX:
          startPositionX !== undefined
            ? startPositionX
            : existing?.startPositionX ?? 0,
        startPositionY:
          startPositionY !== undefined
            ? startPositionY
            : existing?.startPositionY ?? 0
      };
    },
    addElectricalPanel: (
      state,
      action: PayloadAction<{
        levelUniqueId: string;
        electricalPanel: {
          uniqueId: string;
          amperage?: number;
          position?: { x: number; y: number };
          imageAttachmentIds?: number[];
          analyzing?: boolean;
        };
      }>
    ) => {
      const { levelUniqueId, electricalPanel } = action.payload;
      const level = state.structure.find((l) => l.uniqueId === levelUniqueId);

      if (level) {
        level.electricalPanels.push(electricalPanel);
        level.modified = true;
      }
    },
    updateElectricalPanel: (
      state,
      action: PayloadAction<{
        uniqueId: string;
        updates: {
          amperage?: number;
          position?: { x: number; y: number };
          imageAttachmentIds?: number[];
          analyzing?: boolean;
        };
      }>
    ) => {
      const { uniqueId, updates } = action.payload;
      const level = state.structure.find((l) =>
        l.electricalPanels.some((p) => p.uniqueId === uniqueId)
      );

      if (level) {
        const panelIndex = level.electricalPanels.findIndex(
          (p) => p.uniqueId === uniqueId
        );

        if (panelIndex !== -1) {
          level.electricalPanels[panelIndex] = {
            ...level.electricalPanels[panelIndex],
            ...updates
          };
          level.modified = true;
        }
      }
    },
    deleteElectricalPanel: (
      state,
      action: PayloadAction<{
        uniqueId: string;
      }>
    ) => {
      const { uniqueId } = action.payload;
      const level = state.structure.find((l) =>
        l.electricalPanels.some((p) => p.uniqueId === uniqueId)
      );

      if (level) {
        level.electricalPanels = level.electricalPanels.filter(
          (p) => p.uniqueId !== uniqueId
        );
        level.modified = true;
      }
    },
    // Un-place (AHA-6681 batch 2): the panel stays on its level (NOT NULL
    // column server-side) but the position KEY is deleted, so the save path
    // writes the -1 unplaced sentinel again.
    unplaceElectricalPanel: (
      state,
      action: PayloadAction<{ uniqueId: string }>
    ) => {
      const { uniqueId } = action.payload;
      const level = state.structure.find((l) =>
        l.electricalPanels.some((p) => p.uniqueId === uniqueId)
      );
      if (!level) {
        return;
      }
      const panel = level.electricalPanels.find(
        (p) => p.uniqueId === uniqueId
      )!;
      delete (panel as { position?: unknown }).position;
      level.modified = true;
    },
    // Cross-level placement (AHA-6681 batch 2): dropping a panel while
    // viewing a different level moves it there. Both levels are marked
    // modified so the per-level autosave persists the removal AND the
    // addition regardless of save order.
    moveElectricalPanelToLevel: (
      state,
      action: PayloadAction<{
        uniqueId: string;
        targetLevelUniqueId: string;
        position: { x: number; y: number };
      }>
    ) => {
      const { uniqueId, targetLevelUniqueId, position } = action.payload;
      const sourceLevel = state.structure.find((l) =>
        l.electricalPanels.some((p) => p.uniqueId === uniqueId)
      );
      const targetLevel = state.structure.find(
        (l) => l.uniqueId === targetLevelUniqueId
      );
      if (!sourceLevel || !targetLevel) {
        return;
      }
      if (sourceLevel.uniqueId === targetLevel.uniqueId) {
        const panel = sourceLevel.electricalPanels.find(
          (p) => p.uniqueId === uniqueId
        )!;
        panel.position = position;
        sourceLevel.modified = true;
        return;
      }
      const panelIndex = sourceLevel.electricalPanels.findIndex(
        (p) => p.uniqueId === uniqueId
      );
      const [panel] = sourceLevel.electricalPanels.splice(panelIndex, 1);
      targetLevel.electricalPanels.push({ ...panel, position });
      sourceLevel.modified = true;
      targetLevel.modified = true;
    },
    setFootprint: (state, action: PayloadAction<PositionType[]>) => {
      state.footprint = action.payload;
    },
    setLevelFootprint: (
      state,
      action: PayloadAction<{ levelUniqueId: string } & FootprintCommitPayload>
    ) => {
      const level = state.structure.find(
        (l) => l.uniqueId === action.payload.levelUniqueId
      );
      if (level) {
        level.footprint = action.payload.footprint;
        level.footprintShapes = action.payload.footprintShapes;
        level.modified = true;
      }
    },
    clearFootprint: (state) => {
      state.footprint = null;
    },
    updateFootprintPoint: (
      state,
      action: PayloadAction<{ index: number; point: PositionType }>
    ) => {
      if (state.footprint && action.payload.index < state.footprint.length) {
        state.footprint[action.payload.index] = action.payload.point;
      }
    },
    setCondenserLocations: (
      state,
      action: PayloadAction<CondenserLocationType[] | null>
    ) => {
      state.condenserLocations = action.payload;
    },
    toggleCondenserLocation: (
      state,
      action: PayloadAction<CondenserLocationType>
    ) => {
      const current = state.condenserLocations ?? [];
      state.condenserLocations = current.includes(action.payload)
        ? current.filter((location) => location !== action.payload)
        : [...current, action.payload];
    },
    clearCondenserLocations: (state) => {
      state.condenserLocations = null;
    },
    updateMultipleSpaces: (
      state,
      action: PayloadAction<
        {
          uniqueId: string;
          position?: PositionType;
          width?: number;
          length?: number;
        }[]
      >
    ) => {
      const updates = action.payload;
      const updateMap = new Map(updates.map((u) => [u.uniqueId, u]));

      state.structure.forEach((level) => {
        level.sections.forEach((section) => {
          section.spaces.forEach((space) => {
            const update = updateMap.get(space.uniqueId);
            if (update) {
              if (update.position) space.position = update.position;
              if (update.width !== undefined) space.width = update.width;
              if (update.length !== undefined) space.length = update.length;
              space.modified = true;
            }
          });
        });
      });
    },
    setSuggestedSpaces: (
      state,
      action: PayloadAction<{
        levelUniqueId: string;
        suggestedSpaces: string[];
      }>
    ) => {
      const level = state.structure.find(
        (l) => l.uniqueId === action.payload.levelUniqueId
      );
      if (level) {
        level.suggestedSpaces = action.payload.suggestedSpaces;
        level.modified = true;
      }
    },
    setSpaceExtensionRecords: (
      state,
      action: PayloadAction<{ spaceId: string; records: AutoExtendedEdge[] }>
    ) => {
      const { spaceId, records } = action.payload;
      if (records.length === 0) {
        delete state.spaceExtensionRecords[spaceId];
        return;
      }
      state.spaceExtensionRecords[spaceId] = records;
    },
    clearSpaceExtensionRecords: (
      state,
      action: PayloadAction<{ spaceId: string }>
    ) => {
      delete state.spaceExtensionRecords[action.payload.spaceId];
    }
  }
});

export const selectSpaceExtensionRecords = (
  state: { structure: StructureState },
  spaceId: string
): AutoExtendedEdge[] => state.structure.spaceExtensionRecords[spaceId] ?? [];

export const selectStructureSavePending = (state: {
  structure: StructureState;
}): boolean =>
  state.structure.structureSaveRequestedSeq >
  state.structure.structureSaveCompletedSeq;

export const {
  setStructureUniqueId,
  setIsNewlyCreatedStructure,
  setHybridAutobuildWindowsPending,
  addAutoLayoutSpaceIds,
  removeAutoLayoutSpaceIds,
  setAutoLayoutSpaceIds,
  setStructureId,
  setStructureName,
  setStructureNameByUser,
  setStructureActive,
  setStructureOptions,
  restoreStructure,
  setStructure,
  normalizeLevelOrder,
  updateStructureItem,
  updateStructureItemAttribute,
  updateSpaceSizingAttributes,
  applyAttributesToEntireLevel,
  rotateAllWallExposures,
  updateStructureBTUs,
  deleteStructureItem,
  setStructureLoading,
  setConditionedLevels,
  setSupplementalMiniSplitSpaces,
  setExistingSystems,
  updateExistingSystem,
  updateExistingSystemComponent,
  addEmptyExistingSystem,
  addExistingEquipmentComponent,
  addExistingSystem,
  removeExistingSystem,
  deleteExistingSystemComponent,
  unplaceExistingSystemComponent,
  beginSystemCompatibleCheck,
  finishSystemCompatibleCheck,
  failSystemCompatibleCheck,
  restoreSystemCompatibleResults,
  resetRecommendationSelections,
  clearExistingEquipmentForNoAnswer,
  setWantCompositionPicks,
  hydrateRecommendationSelections,
  hydrateRecommendationCatalogCodes,
  setSystemRecommendationCatalogCodes,
  bumpEquipmentSaveVersion,
  bumpEquipmentPersistVersion,
  markStructureSaveRequested,
  markStructureSaveCompleted,
  setSystemRecommendationSelections,
  beginDuctedSlotPreferencesCopy,
  endDuctedSlotPreferencesCopy,
  setSystemTonnageRecord,
  setSystemPtacPreferences,
  setStructureTypeId,
  setStructureSubtypeId,
  setPartialStructure,
  setNotes,
  setStructureAttachments,
  setStructureImageAssignments,
  setSatelliteView,
  assignImageToStructureItem,
  unassignImageFromStructureItem,
  updateStructureImageTransform,
  addElectricalPanel,
  updateElectricalPanel,
  deleteElectricalPanel,
  unplaceElectricalPanel,
  moveElectricalPanelToLevel,
  setFootprint,
  setLevelFootprint,
  clearFootprint,
  updateFootprintPoint,
  setCondenserLocations,
  toggleCondenserLocation,
  clearCondenserLocations,
  updateMultipleSpaces,
  setSuggestedSpaces,
  setSpaceExtensionRecords,
  clearSpaceExtensionRecords
} = structureSlice.actions;

export default structureSlice.reducer;
